'use client';

import { extractVideoId } from '@/helpers/extractVideoId';
import type { Video_Entry } from '@/lib/graphql-sdk';
import { PauseIcon, PlayIcon } from '@heroicons/react/24/solid';
import { useCallback, useEffect, useRef, useState } from 'react';

interface YTPlayer {
    playVideo: () => void;
    pauseVideo: () => void;
    destroy: () => void;
    getPlayerState: () => number;
}

interface YTPlayerOptions {
    videoId: string;
    playerVars: {
        autoplay: number;
        controls: number;
        disablekb: number;
        enablejsapi: number;
        fs: number;
        iv_load_policy: number;
        loop: number;
        modestbranding: number;
        mute: number;
        playsinline: number;
        rel: number;
        showinfo: number;
        start: number;
        playlist: string;
    };
    events: {
        onReady: (event: { target: YTPlayer }) => void;
    };
}

declare global {
    interface Window {
        YT: {
            Player: new (elementId: string, options: YTPlayerOptions) => YTPlayer;
            PlayerState: {
                PLAYING: number;
                PAUSED: number;
            };
        };
        onYouTubeIframeAPIReady: () => void;
    }
}

export default function YouTubePlayer({ video }: { video: Video_Entry }) {
    const [isPlaying, setIsPlaying] = useState(true);
    const [isClient, setIsClient] = useState(false);
    const playerRef = useRef<YTPlayer | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const videoUrl = video?.videoUrl?.url || '';
    const videoId = extractVideoId(videoUrl) as string;

    useEffect(() => {
        setIsClient(true);
    }, []);

    useEffect(() => {
        if (!isClient) return;

        if (!document.getElementById('youtube-iframe-api')) {
            const tag = document.createElement('script');
            tag.id = 'youtube-iframe-api';
            tag.src = 'https://www.youtube.com/iframe_api';
            document.head.appendChild(tag);
        }
    }, [isClient]);

    useEffect(() => {
        if (!isClient) return;

        const initializePlayer = () => {
            const container = document.getElementById('youtube-background');
            if (!container) return;

            if (playerRef.current) return;

            playerRef.current = new window.YT.Player('youtube-background', {
                videoId: videoId,
                playerVars: {
                    autoplay: 1,
                    controls: 0,
                    disablekb: 1,
                    enablejsapi: 1,
                    fs: 0,
                    iv_load_policy: 3,
                    loop: 1,
                    modestbranding: 1,
                    mute: 1,
                    playsinline: 1,
                    rel: 0,
                    showinfo: 0,
                    start: 5,
                    playlist: videoId,
                },
                events: {
                    onReady: (event: { target: YTPlayer }) => event.target.playVideo(),
                },
            });
        };

        if (window.YT?.Player) {
            initializePlayer();
        } else {
            window.onYouTubeIframeAPIReady = initializePlayer;
        }

        return () => {
            playerRef.current?.destroy?.();
            playerRef.current = null;
        };
    }, [isClient, videoId]);

    const togglePlayPause = useCallback(() => {
        if (!playerRef.current) return;

        if (isPlaying) {
            playerRef.current.pauseVideo();
        } else {
            playerRef.current.playVideo();
        }

        setIsPlaying(!isPlaying);
    }, [isPlaying]);

    return (
        <div className="relative">
            <div className="absolute inset-0 z-10" />
            <div
                className="video-background h-1/2-vh w-full max-w-full overflow-hidden sm:h-120 lg:h-120"
                style={{ zIndex: 0 }}
                aria-hidden="true"
                tabIndex={-1}
                ref={containerRef}
            >
                <div
                    className="pointer-events-none relative h-full w-full select-none"
                    aria-hidden="true"
                    tabIndex={-1}
                >
                    <div className="absolute inset-0">
                        <div
                            id="youtube-background"
                            className="absolute top-[-100%] left-[-100%] h-[300%] w-[300%]"
                            tabIndex={-1}
                        />
                    </div>
                </div>
                <div className="absolute inset-0 z-20 bg-transparent" />

                <button
                    type="button"
                    onClick={togglePlayPause}
                    className="absolute right-4 bottom-4 z-30 rounded-full bg-haug-coral-100 p-3 font-bold text-haug-ivory-30 shadow-lg transition duration-300 hover:bg-haug-coral-70 focus:outline-none focus:ring-2 focus:ring-haug-ivory-100"
                    aria-label={isPlaying ? 'Pause background video' : 'Play background video'}
                >
                    {isPlaying ? (
                        <PauseIcon className="h-6 w-6" aria-hidden="true" />
                    ) : (
                        <PlayIcon className="h-6 w-6" aria-hidden="true" />
                    )}
                    <span className="sr-only">
                        {isPlaying ? 'Pause background video' : 'Play background video'}
                    </span>
                </button>
            </div>
        </div>
    );
}
